import {api} from '../../api.ts';
import {unwrapData} from '../../apiResponse.ts';
import type {
    CallRestrictionRule,
    CallRestrictions,
    CheckInput,
    CheckResult,
    DecisionSource,
    Destination,
    DestinationType,
    GroupScope,
    NumberScope,
    PersonScope,
    PhoneHqEntry,
    PhoneHqSource,
    RuleAction,
    RuleInput,
    RulePurpose,
    RuleScope,
    ScopeId,
    ScopeMode,
} from './callRestrictionsTypes';

const BASE = '/api/v1/call-restrictions';

type Raw = Record<string, unknown>;

const str = (value: unknown): string | undefined =>
    value === undefined || value === null || value === '' ? undefined : String(value);

const oneOf = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
    allowed.includes(value as T) ? value as T : fallback;

/** BigDecimal arrives as a JSON number; null/absent means "no limit". */
export const toDecimal = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const DESTINATION_TYPES: readonly DestinationType[] = ['COUNTRY', 'PREFIX', 'NUMBER', 'RANGE', 'CATEGORY'];
const SOURCES: readonly DecisionSource[] = ['global', 'phonehq_default', 'tenant', 'company', 'group', 'person', 'number', 'price_limit', 'emergency', 'none'];

export const readDestination = (raw: unknown): Destination => {
    const d = (raw ?? {}) as Raw;
    return {
        type: oneOf(d.type, DESTINATION_TYPES, 'NUMBER'),
        value: str(d.value),
        from: str(d.from),
        to: str(d.to),
    };
};

const readAppliesTo = (raw: unknown): RulePurpose[] =>
    (Array.isArray(raw) ? raw : []).filter((p): p is RulePurpose => p === 'CALL' || p === 'FORWARD');

const readMode = (raw: unknown): ScopeMode | null => (raw === 'BLOCK_LIST' || raw === 'ALLOW_LIST' ? raw : null);

export const readRule = (raw: unknown, scopeId: ScopeId): CallRestrictionRule => {
    const r = (raw ?? {}) as Raw;
    return {
        id: String(r.id),
        scopeId: str(r.scopeId) ?? scopeId,
        action: oneOf<RuleAction>(r.action, ['ALLOW', 'BLOCK'], 'BLOCK'),
        destination: readDestination(r.destination),
        appliesTo: readAppliesTo(r.appliesTo),
        note: str(r.note) ?? null,
        label: String(r.label ?? ''),
    };
};

/** `CallRestrictionScopeResponse`, also the body of the scope and price limit PUTs. */
export const readScope = (raw: unknown): RuleScope => {
    const s = (raw ?? {}) as Raw;
    const scopeId = String(s.scopeId ?? 'company');
    return {
        scopeId,
        mode: readMode(s.mode),
        priceLimitPerMinute: toDecimal(s.priceLimitPerMinute),
        rules: (Array.isArray(s.rules) ? s.rules : []).map((r) => readRule(r, scopeId)),
    };
};

export const readSource = (raw: unknown): DecisionSource => oneOf(raw, SOURCES, 'none');

const readPhoneHq = (raw: unknown): PhoneHqEntry => {
    const e = (raw ?? {}) as Raw;
    return {
        id: String(e.id),
        source: oneOf<PhoneHqSource>(e.source, ['global', 'phonehq_default', 'tenant'], 'tenant'),
        action: oneOf<RuleAction>(e.action, ['ALLOW', 'BLOCK'], 'BLOCK'),
        destination: readDestination(e.destination),
        appliesTo: readAppliesTo(e.appliesTo),
        label: String(e.label ?? ''),
        destinationLabel: String(e.destinationLabel ?? ''),
        kind: e.kind === 'allow_list' ? 'allow_list' : 'rule',
        msisdn: str(e.msisdn)?.replace(/\D/g, '') || undefined,
    };
};

export const readCallRestrictions = (response: unknown): CallRestrictions => {
    const data = (unwrapData(response as Raw) ?? {}) as Raw;
    const groups: GroupScope[] = (Array.isArray(data.groups) ? data.groups : []).map((g) => {
        const raw = (g ?? {}) as Raw;
        return {...readScope(raw), groupId: String(raw.groupId), name: str(raw.name) ?? null, memberCount: toDecimal(raw.memberCount)};
    });
    const people: PersonScope[] = (Array.isArray(data.people) ? data.people : []).map((p) => {
        const raw = (p ?? {}) as Raw;
        return {...readScope(raw), userId: String(raw.userId), name: str(raw.name) ?? null};
    });
    const numbers: NumberScope[] = (Array.isArray(data.numbers) ? data.numbers : []).map((n) => {
        const raw = (n ?? {}) as Raw;
        const scope = readScope(raw);
        const msisdn = str(raw.msisdn) ?? scope.scopeId.replace(/^number-/, '');
        return {...scope, msisdn, locked: raw.locked === true};
    });
    const priceLimits = (data.priceLimits ?? {}) as Raw;
    return {
        editable: data.editable !== false,
        tenantMode: readMode(data.tenantMode),
        company: readScope(data.company),
        groups,
        people,
        numbers,
        lockedNumbers: (Array.isArray(data.lockedNumbers) ? data.lockedNumbers : []).map(String),
        phonehq: (Array.isArray(data.phonehq) ? data.phonehq : []).map(readPhoneHq),
        priceLimits: {currency: String(priceLimits.currency ?? ''), maximumPerMinute: toDecimal(priceLimits.maximumPerMinute)},
        categories: (Array.isArray(data.categories) ? data.categories : []).map((c) => {
            const cat = (c ?? {}) as Raw;
            return {id: String(cat.id), name: String(cat.name ?? cat.id), prefixes: Array.isArray(cat.prefixes) ? cat.prefixes.map(String) : []};
        }),
    };
};

export const readCheckResult = (response: unknown): CheckResult => {
    const data = (unwrapData(response as Raw) ?? {}) as Raw;
    const decidedBy = (data.decidedBy ?? {}) as Raw;
    const price = data.price as Raw | undefined;
    return {
        allowed: data.allowed === true,
        emergency: data.emergency === true,
        number: String(data.number ?? ''),
        fromNumber: str(data.fromNumber),
        decidedBy: {source: readSource(decidedBy.source), ruleId: str(decidedBy.ruleId) ?? null, label: String(decidedBy.label ?? '')},
        price: price ? {perMinute: Number(price.perMinute), currency: String(price.currency ?? ''), limit: Number(price.limit)} : null,
    };
};

const TAG = 'CallRestrictions' as const;

export const callRestrictionsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getCallRestrictions: builder.query<CallRestrictions, void>({
            query: () => BASE,
            transformResponse: readCallRestrictions,
            providesTags: [TAG],
        }),
        // `mode: null` sets the scope back to neutral (no list of its own).
        setScopeMode: builder.mutation<void, {scopeId: ScopeId; mode: ScopeMode | null}>({
            query: ({scopeId, mode}) => ({url: `${BASE}/scopes/${encodeURIComponent(scopeId)}`, method: 'PUT', body: {mode}}),
            invalidatesTags: [TAG],
        }),
        createRule: builder.mutation<void, RuleInput>({
            query: (body) => ({url: `${BASE}/rules`, method: 'POST', body}),
            invalidatesTags: [TAG],
        }),
        updateRule: builder.mutation<void, {ruleId: string; body: RuleInput}>({
            query: ({ruleId, body}) => ({url: `${BASE}/rules/${encodeURIComponent(ruleId)}`, method: 'PATCH', body}),
            invalidatesTags: [TAG],
        }),
        deleteRule: builder.mutation<void, string>({
            query: (ruleId) => ({url: `${BASE}/rules/${encodeURIComponent(ruleId)}`, method: 'DELETE'}),
            invalidatesTags: [TAG],
        }),
        setPriceLimit: builder.mutation<void, {scopeId: ScopeId; perMinute: number | null}>({
            query: ({scopeId, perMinute}) => ({url: `${BASE}/price-limits/${encodeURIComponent(scopeId)}`, method: 'PUT', body: {perMinute}}),
            invalidatesTags: [TAG],
        }),
        checkNumber: builder.mutation<CheckResult, CheckInput>({
            query: (body) => ({url: `${BASE}/checks`, method: 'POST', body}),
            transformResponse: readCheckResult,
        }),
    }),
});

export const {
    useGetCallRestrictionsQuery,
    useSetScopeModeMutation,
    useCreateRuleMutation,
    useUpdateRuleMutation,
    useDeleteRuleMutation,
    useSetPriceLimitMutation,
    useCheckNumberMutation,
} = callRestrictionsApi;
